import React, { useContext } from 'react';

// chakra imports
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Icon,
  IconButton,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {
  renderThumb,
  renderTrack,
  renderView,
  renderViewMini,
} from 'components/scrollbar/Scrollbar';
import Content from 'components/sidebar/components/Content';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { SidebarContext } from 'context/SidebarContext';

// Assets
import { IoMenuOutline } from 'react-icons/io5';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';

function Sidebar(props) {
  const { routes, mini, collapsed } = props;
  const sidebarContext = useContext(SidebarContext);
  const { sidebarCollapsed = false, setSidebarCollapsed = () => {} } = sidebarContext || {};
  // this is for the rest of the collapses
  let variantChange = '0.2s linear';
  let shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.08)',
    'unset',
  );
  // Chakra Color Mode
  let sidebarBg = useColorModeValue('white', 'navy.800');
  let sidebarRadius = '30px';
  let sidebarMargins = '0px';
  const toggleSidebar = () => {
    console.log('Toggle sidebar clicked, current state:', sidebarCollapsed);
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Determine if sidebar should be collapsed based on screen size or user preference
  // On smaller screens (below xl), always show collapsed. On xl+, respect user preference.
  const getWidth = () => {
    if (sidebarCollapsed) return '40px';
    if (mini === false || mini === true) return '285px';
    return '120px';
  };

  // SIDEBAR
  return (
    <Box
      display="block"
      position="fixed"
      minH="100%"
      zIndex={100}
    >
      <Box
        bg={sidebarBg}
        transition={variantChange}
        w={getWidth()}
        ms={{
          sm: '16px',
        }}
        my={{
          sm: '16px',
        }}
        h="calc(100vh - 32px)"
        m={sidebarMargins}
        borderRadius={sidebarRadius}
        minH="100%"
        overflowX="hidden"
        boxShadow={shadow}
        position="relative"
      >
        {/* Show content when sidebar is not collapsed */}
        <Box display={sidebarCollapsed ? 'none' : 'block'} h="100%">
          <Scrollbars
            autoHide
            renderTrackVertical={renderTrack}
            renderThumbVertical={renderThumb}
            renderView={
              mini === false
                ? renderView
                : mini === true
                ? renderView
                : renderViewMini
            }
          >
            <Content mini={mini} routes={routes} />
          </Scrollbars>
        </Box>
        
        {/* Collapse/expand button visible on all screen sizes */}
        <IconButton
          aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          icon={<Icon as={sidebarCollapsed ? MdChevronRight : MdChevronLeft} w={3} h={3} />}
          onClick={toggleSidebar}
          position="absolute"
          top="50%"
          right={sidebarCollapsed ? "50%" : "8px"}
          transform={sidebarCollapsed ? "translate(50%, -50%)" : "translateY(-50%)"}
          display="flex"
          size="sm"
          minW="28px"
          h="28px"
          bg={sidebarBg}
          border="1.5px solid"
          borderColor={useColorModeValue('gray.200', 'gray.600')}
          boxShadow="md"
          zIndex={1001}
          borderRadius="full"
          _hover={{
            bg: useColorModeValue('gray.50', 'gray.700'),
            transform: sidebarCollapsed ? "translate(50%, -50%) scale(1.05)" : "translateY(-50%) scale(1.05)",
          }}
          _active={{
            bg: useColorModeValue('gray.100', 'gray.600'),
            transform: sidebarCollapsed ? "translate(50%, -50%) scale(0.95)" : "translateY(-50%) scale(0.95)",
          }}
          _focus={{
            boxShadow: 'outline',
          }}
          transition="all 0.2s"
        />
      </Box>
    </Box>
  );
}

// FUNCTIONS
export function SidebarResponsive(props) {
  let sidebarBackgroundColor = useColorModeValue('white', 'navy.800');
  let menuColor = useColorModeValue('gray.400', 'white');
  // // SIDEBAR
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef();

  const { routes } = props;
  // let isWindows = navigator.platform.startsWith("Win");
  //  BRAND

  return (
    <Flex display={{ sm: 'flex', xl: 'none' }} alignItems="center">
      <Flex ref={btnRef} w="max-content" h="max-content" onClick={onOpen}>
        <Icon
          as={IoMenuOutline}
          color={menuColor}
          my="auto"
          w="20px"
          h="20px"
          me="10px"
          _hover={{ cursor: 'pointer' }}
        />
      </Flex>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        placement={document.documentElement.dir === 'rtl' ? 'right' : 'left'}
        finalFocusRef={btnRef}
      >
        <DrawerOverlay />
        <DrawerContent
          w="285px"
          maxW="285px"
          ms={{
            sm: '16px',
          }}
          my={{
            sm: '16px',
          }}
          borderRadius="16px"
          bg={sidebarBackgroundColor}
        >
          <DrawerCloseButton
            zIndex="3"
            onClick={onClose}
            _focus={{ boxShadow: 'none' }}
            _hover={{ boxShadow: 'none' }}
          />
          <DrawerBody maxW="285px" px="0rem" pb="0">
            <Scrollbars
              autoHide
              renderTrackVertical={renderTrack}
              renderThumbVertical={renderThumb}
              renderView={renderView}
            >
              <Content mini={false} routes={routes} />
            </Scrollbars>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}
// PROPS

export default Sidebar;
